import { addAllToRecord, addToRecord } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  AllNetworkGroupsLoadFulfilledAction,
  AllNetworkGroupsLoadMoreFulfilledAction,
} from '../../domains/allNetworkGroups/actions.types';
import { FirstRunSuggestedGroupsLoadFulfilledAction } from '../../domains/firstRunSuggestedGroups/actions.types';
import {
  GroupAddUserByEmailFulfilledAction,
  GroupAddUserFulfilledAction,
  GroupRemoveUserFulfilledAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import { GroupRelatedGroupsLoadFulfilledAction } from '../../domains/groupRelatedGroupsLoad/actions.types';
import { ResolveGroupMembershipFromMembershipPanelFulfilledAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import { GroupsMembersLoadFulfilledAction } from '../../domains/groupsMembers/actions.types';
import {
  OfficialGroupsLoadFulfilledAction,
  OfficialGroupsLoadMoreFulfilledAction,
} from '../../domains/officialGroups/actions.types';
import {
  RecommendedGroupsLoadFulfilledAction,
  RecommendedGroupsLoadMoreFulfilledAction,
} from '../../domains/recommendedGroups/actions.types';
import { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import { SuggestedActionsLoadFulfilledAction } from '../../domains/suggestedActions/actions.types';
import {
  SuggestedGroupsLoadFulfilledAction,
  SuggestedGroupsLoadMoreFulfilledAction,
} from '../../domains/suggestedGroups/actions.types';
import { SuggestedMyLeaderGroupsLoadFulfilledAction } from '../../domains/suggestedMyLeaderGroups/actions.types';
import { UserSidebarLoadFulfilledAction } from '../../domains/userSidebarLoad/actions.types';
import { Group } from '../types';

import { GroupMemberCounts } from './types';

export type GroupMemberCountsAction =
  | GroupSidebarLoadFulfilledAction
  | GroupRelatedGroupsLoadFulfilledAction
  | GroupsMembersLoadFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | ResolveGroupMembershipFromMembershipPanelFulfilledAction
  | GroupAddUserByEmailFulfilledAction
  | GroupAddUserFulfilledAction
  | GroupRemoveUserFulfilledAction
  | RecommendedGroupsLoadFulfilledAction
  | RecommendedGroupsLoadMoreFulfilledAction
  | SuggestedActionsLoadFulfilledAction
  | FirstRunSuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadMoreFulfilledAction
  | AllNetworkGroupsLoadFulfilledAction
  | UserSidebarLoadFulfilledAction
  | AllNetworkGroupsLoadMoreFulfilledAction
  | SuggestedMyLeaderGroupsLoadFulfilledAction
  | OfficialGroupsLoadFulfilledAction
  | OfficialGroupsLoadMoreFulfilledAction;

const initialState: GroupMemberCounts = {};

const addMemberToGroup = (state: GroupMemberCounts, groupId: Group['id']) => {
  const groupMemberCount = state[groupId];

  if (groupMemberCount == null) {
    return state;
  }

  const updatedGroupMemberCount = {
    ...groupMemberCount,
    totalMemberCount: groupMemberCount.totalMemberCount + 1,
  };

  return addToRecord(state, groupId, updatedGroupMemberCount);
};

const removeMemberFromGroup = (state: GroupMemberCounts, groupId: Group['id']) => {
  const groupMemberCount = state[groupId];

  if (groupMemberCount == null) {
    return state;
  }

  const updatedGroupMemberCount = {
    ...groupMemberCount,
    totalMemberCount: groupMemberCount.totalMemberCount - 1,
  };

  return addToRecord(state, groupId, updatedGroupMemberCount);
};

const handleFulfilledMembershipRequest = (
  state: GroupMemberCounts,
  payload: ResolveGroupMembershipFromMembershipPanelFulfilledAction['payload']
) => {
  const membershipRequestType = payload.requestType;

  return membershipRequestType === 'approve' ? addMemberToGroup(state, payload.groupId) : state;
};

const reducer: Reducer<GroupMemberCounts, GroupMemberCountsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_SIDEBAR.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'USER_SIDEBAR.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'GROUPS_MEMBERS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SEARCH.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return handleFulfilledMembershipRequest(state, action.payload);
    case 'GROUP.ADD_USER_FULFILLED':
      return addMemberToGroup(state, action.payload.groupId);
    case 'GROUP.ADD_USER_BY_EMAIL_FULFILLED':
      return addMemberToGroup(state, action.payload.groupId);
    case 'GROUP.REMOVE_USER_FULFILLED':
      return removeMemberFromGroup(state, action.payload.groupId);
    case 'ALL_NETWORK_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'GROUP_RELATED_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'RECOMMENDED_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'RECOMMENDED_GROUPS.LOAD_MORE_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SUGGESTED_ACTION.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'FRE_SUGGESTED_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SUGGESTED_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SUGGESTED_GROUPS.LOAD_MORE_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'SUGGESTED_MY_LEADER_GROUPS.LOAD_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    case 'OFFICIAL_GROUPS.LOAD_FULFILLED':
    case 'OFFICIAL_GROUPS.LOAD_MORE_FULFILLED':
      return addAllToRecord(state, action.payload.entities.groupMemberCounts);
    default:
      return state;
  }
};

export default reducer;
